import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import UndoIcon from '@mui/icons-material/Undo';
import SaveIcon from '@mui/icons-material/Save';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { timesheetsApi, Timesheet, TimesheetEntry, CreateTimesheetData } from '../services/timesheets';
import { clientsApi, Client } from '../services/clients';
import { format, parseISO, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns';
import InvoiceDateCalendar from '../components/InvoiceDateCalendar';
import TimesheetMonthView from '../components/TimesheetMonthView';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useCompany } from '../contexts/CompanyContext';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

type TimesheetFormData = Omit<CreateTimesheetData, 'entries'> & {
  entries: (Omit<TimesheetEntry, 'date'> & { date: Date | string })[];
};

const Timesheets: React.FC = () => {
  const { company } = useCompany();
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState<Timesheet | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [timesheetToDelete, setTimesheetToDelete] = useState<Timesheet | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [billableFilter, setBillableFilter] = useState<string>('all'); // 'all', 'billable', 'non-billable'
  const [dateFilter, setDateFilter] = useState<{ start?: string; end?: string }>({});
  const [dialogTab, setDialogTab] = useState(0); // 0 = Quick Add, 1 = Manual Entry
  const [quickAddDates, setQuickAddDates] = useState<Date[]>([]);
  const [quickAddHours, setQuickAddHours] = useState<number>(8);
  const [quickAddClientId, setQuickAddClientId] = useState<string>('');
  const [quickAddDescription, setQuickAddDescription] = useState<string>('');
  const [quickAddIsBillable, setQuickAddIsBillable] = useState<boolean>(true);
  const [quickAddCategory, setQuickAddCategory] = useState<string>('');
  const [dateEntriesDialogOpen, setDateEntriesDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [dateEntries, setDateEntries] = useState<Array<{ entry: TimesheetEntry; timesheet: Timesheet; entryIndex: number }>>([]);
  const [editedEntries, setEditedEntries] = useState<Map<string, Partial<TimesheetEntry>>>(new Map());

  const { register, handleSubmit, control, reset, watch, setValue, formState: { errors } } = useForm<TimesheetFormData>({
    defaultValues: {
      startDate: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      endDate: format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      status: 'draft',
      entries: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'entries',
  });

  const watchedStartDate = watch('startDate');
  const watchedEndDate = watch('endDate');

  useEffect(() => {
    loadClients();
    loadTimesheets();
  }, [statusFilter, dateFilter, billableFilter, company?.enableNonBillableTime]);

  const loadClients = async () => {
    try {
      const data = await clientsApi.getAll();
      setClients(data);
    } catch (err: any) {
      console.error('Failed to load clients:', err);
    }
  };

  const loadTimesheets = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters: any = {};
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (dateFilter.start) filters.startDate = dateFilter.start;
      if (dateFilter.end) filters.endDate = dateFilter.end;
      
      let data = await timesheetsApi.getAll(filters);
      
      // Filter by billable status if enabled
      if (company?.enableNonBillableTime && billableFilter !== 'all') {
        data = data.map(timesheet => ({
          ...timesheet,
          entries: timesheet.entries.filter(entry => {
            const isBillable = entry.isBillable !== false; // Default to true
            if (billableFilter === 'billable') return isBillable;
            if (billableFilter === 'non-billable') return !isBillable;
            return true;
          })
        })).filter(timesheet => timesheet.entries.length > 0);
      }
      
      setTimesheets(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  };

  const getClientById = (clientId: string): Client | undefined => {
    return clients.find(c => c.id === clientId);
  };

  const getClientContractDates = (clientId: string): { start?: Date; end?: Date } => {
    const client = getClientById(clientId);
    if (!client || client.customerType !== 'Contract') return {};
    
    return {
      start: client.contractStartDate ? parseISO(client.contractStartDate) : undefined,
      end: client.contractEndDate ? parseISO(client.contractEndDate) : undefined,
    };
  };

  const isDateWithinContract = (date: Date, clientId: string): boolean => {
    const contractDates = getClientContractDates(clientId);
    if (!contractDates.start || !contractDates.end) return true; // No contract dates = allow all
    
    return isWithinInterval(date, {
      start: contractDates.start,
      end: contractDates.end,
    });
  };

  const handleOpenDialog = (timesheet?: Timesheet) => {
    if (timesheet) {
      setEditingTimesheet(timesheet);
      setDialogTab(1); // Manual entry for editing
      reset({
        startDate: timesheet.startDate,
        endDate: timesheet.endDate,
        status: timesheet.status,
        entries: timesheet.entries.map(entry => ({
          ...entry,
          date: parseISO(entry.date),
          isBillable: entry.isBillable !== false, // Default to true
          category: entry.category,
        })),
      });
    } else {
      setEditingTimesheet(null);
      setDialogTab(0); // Quick Add for new timesheet
      const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
      const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 });
      reset({
        startDate: format(weekStart, 'yyyy-MM-dd'),
        endDate: format(weekEnd, 'yyyy-MM-dd'),
        status: 'draft',
        entries: [],
      });
      setQuickAddDates([]);
      setQuickAddHours(8);
      setQuickAddClientId('');
      setQuickAddDescription('');
      setQuickAddCategory('');
    }
    setOpenDialog(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTimesheet(null);
    reset();
    setError(null);
  };

  const handleQuickAdd = async () => {
    if (quickAddDates.length === 0) {
      setError('Please select at least one date');
      return;
    }
    if (!quickAddClientId) {
      setError('Please select a client');
      return;
    }
    if (!quickAddDescription.trim()) {
      setError('Please enter a project description');
      return;
    }

    try {
      setError(null);
      
      // Sort dates to get min and max
      const sortedDates = [...quickAddDates].sort((a, b) => a.getTime() - b.getTime());
      const startDate = format(sortedDates[0], 'yyyy-MM-dd');
      const endDate = format(sortedDates[sortedDates.length - 1], 'yyyy-MM-dd');

      // Use flood-fill for each date range (handles consecutive dates)
      // Group consecutive dates
      const dateGroups: Date[][] = [];
      let currentGroup: Date[] = [sortedDates[0]];

      for (let i = 1; i < sortedDates.length; i++) {
        const prevDate = sortedDates[i - 1];
        const currDate = sortedDates[i];
        const daysDiff = Math.floor((currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysDiff === 1) {
          // Consecutive date
          currentGroup.push(currDate);
        } else {
          // Gap found, start new group
          dateGroups.push(currentGroup);
          currentGroup = [currDate];
        }
      }
      dateGroups.push(currentGroup);

      // Flood fill each group
      for (const group of dateGroups) {
        const groupStart = format(group[0], 'yyyy-MM-dd');
        const groupEnd = format(group[group.length - 1], 'yyyy-MM-dd');
        
        await timesheetsApi.floodFill({
          clientId: quickAddClientId,
          startDate: groupStart,
          endDate: groupEnd,
          hours: quickAddHours,
          projectDescription: quickAddDescription,
          isBillable: company?.enableNonBillableTime ? quickAddIsBillable : true,
          category: !quickAddIsBillable && quickAddCategory ? quickAddCategory : undefined,
        });
      }

      handleCloseDialog();
      loadTimesheets();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create timesheet entries');
    }
  };

  const onSubmit = async (data: TimesheetFormData) => {
    try {
      setError(null);
      const submitData: CreateTimesheetData = {
        startDate: data.startDate,
        endDate: data.endDate,
        status: data.status,
        entries: data.entries.map(entry => ({
          ...entry,
          date: entry.date instanceof Date ? format(entry.date, 'yyyy-MM-dd') : entry.date,
          isBillable: company?.enableNonBillableTime ? (entry.isBillable !== undefined ? entry.isBillable : true) : true,
          category: company?.enableNonBillableTime && entry.isBillable === false ? entry.category : undefined,
        })),
      };

      if (editingTimesheet) {
        await timesheetsApi.update(editingTimesheet.id, submitData);
      } else {
        await timesheetsApi.create(submitData);
      }
      handleCloseDialog();
      loadTimesheets();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save timesheet');
    }
  };

  const handleDeleteClick = (timesheet: Timesheet) => {
    setTimesheetToDelete(timesheet);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (timesheetToDelete) {
      try {
        setError(null);
        await timesheetsApi.delete(timesheetToDelete.id);
        setDeleteDialogOpen(false);
        setTimesheetToDelete(null);
        loadTimesheets();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete timesheet');
      }
    }
  };

  const handleStatusChange = async (timesheet: Timesheet, newStatus: 'draft' | 'submitted' | 'approved' | 'invoiced') => {
    try {
      setError(null);
      await timesheetsApi.update(timesheet.id, { status: newStatus });
      loadTimesheets();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleDateClick = (date: Date, entries: TimesheetEntry[]) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Find all timesheets that contain entries for this date
    const entriesWithTimesheets: Array<{ entry: TimesheetEntry; timesheet: Timesheet; entryIndex: number }> = [];
    
    timesheets.forEach(timesheet => {
      timesheet.entries.forEach((entry, entryIndex) => {
        if (entry.date === dateStr) {
          entriesWithTimesheets.push({ entry, timesheet, entryIndex });
        }
      });
    });
    
    setSelectedDate(date);
    setDateEntries(entriesWithTimesheets);
    setDateEntriesDialogOpen(true);
  };

  const handleCloseDateEntriesDialog = () => {
    setDateEntriesDialogOpen(false);
    setSelectedDate(null);
    setDateEntries([]);
    setEditedEntries(new Map());
  };

  const handleEntryFieldChange = (timesheetId: string, entryIndex: number, field: keyof TimesheetEntry, value: any) => {
    const key = `${timesheetId}-${entryIndex}`;
    const currentEdits = editedEntries.get(key) || {};
    setEditedEntries(new Map(editedEntries.set(key, { ...currentEdits, [field]: value })));
  };

  const handleSaveEntry = async (timesheet: Timesheet, entryIndex: number) => {
    try {
      setError(null);
      const key = `${timesheet.id}-${entryIndex}`;
      const edits = editedEntries.get(key);
      
      if (!edits || Object.keys(edits).length === 0) {
        return; // No changes to save
      }

      const updatedEntries = [...timesheet.entries];
      updatedEntries[entryIndex] = { ...updatedEntries[entryIndex], ...edits };
      
      await timesheetsApi.update(timesheet.id, {
        startDate: timesheet.startDate,
        endDate: timesheet.endDate,
        status: timesheet.status,
        entries: updatedEntries,
      });
      
      // Remove from edited entries
      const newEditedEntries = new Map(editedEntries);
      newEditedEntries.delete(key);
      setEditedEntries(newEditedEntries);
      
      await loadTimesheets();
      
      // Refresh the date entries dialog
      if (selectedDate) {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const entriesWithTimesheets: Array<{ entry: TimesheetEntry; timesheet: Timesheet; entryIndex: number }> = [];
        
        const refreshedTimesheets = await timesheetsApi.getAll();
        refreshedTimesheets.forEach(ts => {
          ts.entries.forEach((entry, idx) => {
            if (entry.date === dateStr) {
              entriesWithTimesheets.push({ entry, timesheet: ts, entryIndex: idx });
            }
          });
        });
        
        setDateEntries(entriesWithTimesheets);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update entry');
    }
  };

  const handleDeleteEntry = async (timesheet: Timesheet, entryIndex: number) => {
    try {
      setError(null);
      const updatedEntries = timesheet.entries.filter((_, index) => index !== entryIndex);
      
      // Remove from edited entries if it exists
      const key = `${timesheet.id}-${entryIndex}`;
      const newEditedEntries = new Map(editedEntries);
      newEditedEntries.delete(key);
      setEditedEntries(newEditedEntries);
      
      // If no entries left, delete the entire timesheet
      if (updatedEntries.length === 0) {
        await timesheetsApi.delete(timesheet.id);
      } else {
        // Update timesheet with remaining entries
        await timesheetsApi.update(timesheet.id, {
          startDate: timesheet.startDate,
          endDate: timesheet.endDate,
          status: timesheet.status,
          entries: updatedEntries,
        });
      }
      
      await loadTimesheets();
      
      // Refresh the date entries dialog
      if (selectedDate) {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const entriesWithTimesheets: Array<{ entry: TimesheetEntry; timesheet: Timesheet; entryIndex: number }> = [];
        
        const refreshedTimesheets = await timesheetsApi.getAll();
        refreshedTimesheets.forEach(ts => {
          ts.entries.forEach((entry, idx) => {
            if (entry.date === dateStr) {
              entriesWithTimesheets.push({ entry, timesheet: ts, entryIndex: idx });
            }
          });
        });
        
        setDateEntries(entriesWithTimesheets);
        
        // Close dialog if no entries left
        if (entriesWithTimesheets.length === 0) {
          handleCloseDateEntriesDialog();
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete entry');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'submitted': return 'info';
      case 'approved': return 'success';
      case 'invoiced': return 'secondary';
      default: return 'default';
    }
  };

  const getTotalHours = (entries: TimesheetEntry[]) => {
    return entries.reduce((sum, entry) => sum + entry.hours, 0);
  };

  if (loading && timesheets.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Timesheets</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          New Timesheet
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Month View */}
      <Box sx={{ mb: 3 }}>
        <TimesheetMonthView
          timesheets={timesheets}
          clients={clients}
          onDateClick={handleDateClick}
        />
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="invoiced">Invoiced</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {company?.enableNonBillableTime && (
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Billable Status</InputLabel>
                <Select
                  value={billableFilter}
                  label="Billable Status"
                  onChange={(e) => setBillableFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="billable">Billable Only</MenuItem>
                  <MenuItem value="non-billable">Non-Billable Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={dateFilter.start || ''}
              onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value || undefined })}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={dateFilter.end || ''}
              onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value || undefined })}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="outlined"
              onClick={() => setDateFilter({})}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Period</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Entries</TableCell>
              <TableCell>Total Hours</TableCell>
              <TableCell>Clients</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {timesheets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary">No timesheets found. Create your first timesheet to get started.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              timesheets.map((timesheet) => {
                const totalHours = getTotalHours(timesheet.entries);
                const uniqueClients = Array.from(new Set(timesheet.entries.map(e => e.clientId)));
                const clientNames = uniqueClients.map(id => getClientById(id)?.name || 'Unknown').join(', ');
                
                return (
                  <TableRow key={timesheet.id}>
                    <TableCell>
                      {format(parseISO(timesheet.startDate), 'MMM dd')} - {format(parseISO(timesheet.endDate), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={timesheet.status.charAt(0).toUpperCase() + timesheet.status.slice(1)} 
                        size="small" 
                        color={getStatusColor(timesheet.status) as any}
                      />
                    </TableCell>
                    <TableCell>{timesheet.entries.length}</TableCell>
                    <TableCell>{totalHours.toFixed(1)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {clientNames || 'No clients'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={timesheet.status === 'invoiced' ? 'Cannot edit invoiced timesheet' : 'Edit'}>
                        <span>
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog(timesheet)}
                            disabled={timesheet.status === 'invoiced'}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {timesheet.status === 'draft' && (
                        <Tooltip title="Submit">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleStatusChange(timesheet, 'submitted')}
                          >
                            <SendIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {timesheet.status === 'submitted' && (
                        <>
                          <Tooltip title="Recall">
                            <IconButton 
                              size="small" 
                              color="warning"
                              onClick={() => handleStatusChange(timesheet, 'draft')}
                            >
                              <UndoIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Approve">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => handleStatusChange(timesheet, 'approved')}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title={timesheet.status === 'invoiced' ? 'Cannot delete invoiced timesheet' : 'Delete'}>
                        <span>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteClick(timesheet)} 
                            color="error"
                            disabled={timesheet.status === 'invoiced'}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>{editingTimesheet ? 'Edit Timesheet' : 'Create New Timesheet'}</DialogTitle>
        {!editingTimesheet && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tabs value={dialogTab} onChange={(_, newValue) => setDialogTab(newValue)}>
              <Tab label="Quick Add" />
              <Tab label="Manual Entry" />
            </Tabs>
          </Box>
        )}
        {dialogTab === 0 && !editingTimesheet ? (
          <>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Select Dates (Click dates on the calendar to select multiple)
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <InvoiceDateCalendar
                      selectedDates={quickAddDates}
                      onChange={(dates) => {
                        setError(null); // Clear any previous errors
                        // Filter dates based on selected client's contract period
                        if (quickAddClientId) {
                          const contractDates = getClientContractDates(quickAddClientId);
                          if (contractDates.start && contractDates.end) {
                            const filtered = dates.filter(date => 
                              isDateWithinContract(date, quickAddClientId)
                            );
                            if (filtered.length < dates.length) {
                              setError('Some dates were removed as they fall outside the contract period');
                            }
                            setQuickAddDates(filtered);
                          } else {
                            setQuickAddDates(dates);
                          }
                        } else {
                          setQuickAddDates(dates);
                        }
                      }}
                      minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 1))}
                      maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                    />
                  </Box>
                  {quickAddDates.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {quickAddDates.length} date(s) selected: {quickAddDates
                        .sort((a, b) => a.getTime() - b.getTime())
                        .map(d => format(d, 'MMM dd'))
                        .join(', ')}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required error={!quickAddClientId && quickAddDates.length > 0}>
                    <InputLabel>Client</InputLabel>
                    <Select
                      value={quickAddClientId}
                      label="Client"
                      onChange={(e) => {
                        const clientId = e.target.value;
                        setQuickAddClientId(clientId);
                        setError(null); // Clear any previous errors
                        // Filter dates if client has contract dates
                        if (clientId && quickAddDates.length > 0) {
                          const contractDates = getClientContractDates(clientId);
                          if (contractDates.start && contractDates.end) {
                            const filtered = quickAddDates.filter(date => 
                              isDateWithinContract(date, clientId)
                            );
                            if (filtered.length < quickAddDates.length) {
                              setError('Some dates were removed as they fall outside the contract period');
                            }
                            setQuickAddDates(filtered);
                          }
                        }
                      }}
                    >
                      {clients.map((client) => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.name} {client.customerType === 'Contract' ? '(Contract)' : '(T&M)'}
                        </MenuItem>
                      ))}
                    </Select>
                    {!quickAddClientId && quickAddDates.length > 0 && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        Client is required
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Hours per Day"
                    inputProps={{ step: 0.25, min: 0 }}
                    value={quickAddHours}
                    onChange={(e) => setQuickAddHours(parseFloat(e.target.value) || 0)}
                    helperText="Hours to apply to each selected date"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Project Description"
                    value={quickAddDescription}
                    onChange={(e) => setQuickAddDescription(e.target.value)}
                    required
                    helperText="Description for all selected dates"
                  />
                </Grid>
                {company?.enableNonBillableTime && (
                  <>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={quickAddIsBillable}
                            onChange={(e) => setQuickAddIsBillable(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Billable time"
                      />
                    </Grid>
                    {!quickAddIsBillable && company?.nonBillableCategories && company.nonBillableCategories.length > 0 && (
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>Category</InputLabel>
                          <Select
                            value={quickAddCategory || ''}
                            label="Category"
                            onChange={(e) => setQuickAddCategory(e.target.value)}
                          >
                            {company.nonBillableCategories.map((cat) => (
                              <MenuItem key={cat} value={cat}>
                                {cat}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </>
                )}

                {quickAddClientId && (() => {
                  const contractDates = getClientContractDates(quickAddClientId);
                  if (contractDates.start && contractDates.end) {
                    return (
                      <Grid item xs={12}>
                        <Alert severity="info">
                          Contract Period: {format(contractDates.start, 'MMM dd, yyyy')} - {format(contractDates.end, 'MMM dd, yyyy')}
                        </Alert>
                      </Grid>
                    );
                  }
                  return null;
                })()}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button onClick={handleQuickAdd} variant="contained" disabled={quickAddDates.length === 0 || !quickAddClientId || !quickAddDescription.trim()}>
                Add to Timesheet ({quickAddDates.length} {quickAddDates.length === 1 ? 'day' : 'days'})
              </Button>
            </DialogActions>
          </>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent>
              {editingTimesheet?.status === 'invoiced' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This timesheet has been invoiced and cannot be edited or deleted.
                </Alert>
              )}
              <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  {...register('startDate', { required: 'Start date is required' })}
                  error={!!errors.startDate}
                  helperText={errors.startDate?.message}
                  disabled={editingTimesheet?.status === 'invoiced'}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  {...register('endDate', { required: 'End date is required' })}
                  error={!!errors.endDate}
                  helperText={errors.endDate?.message}
                  disabled={editingTimesheet?.status === 'invoiced'}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select {...field} label="Status" disabled={editingTimesheet?.status === 'invoiced'}>
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="submitted">Submitted</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="invoiced">Invoiced</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">Entries</Typography>
                  {editingTimesheet?.status !== 'invoiced' && (
                    <Button size="small" onClick={() => append({ date: new Date(), hours: 0, projectDescription: '', clientId: '' })}>
                      Add Entry
                    </Button>
                  )}
                </Box>
              </Grid>

              {fields.map((field, index) => {
                const entryClientId = watch(`entries.${index}.clientId`);
                const contractDates = entryClientId ? getClientContractDates(entryClientId) : {};
                const entryDate = watch(`entries.${index}.date`);
                const isDateValid = !entryClientId || !entryDate || 
                  (entryDate instanceof Date && isDateWithinContract(entryDate, entryClientId));

                return (
                  <Grid container spacing={2} key={field.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Grid item xs={12} sm={3}>
                      <Controller
                        name={`entries.${index}.date`}
                        control={control}
                        rules={{ 
                          required: 'Date is required',
                          validate: (value) => {
                            if (!entryClientId) return true;
                            if (!value) return true;
                            const date = value instanceof Date ? value : parseISO(value);
                            if (!isDateWithinContract(date, entryClientId)) {
                              return 'Date must be within contract period';
                            }
                            return true;
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            type="date"
                            fullWidth
                            label="Date"
                            InputLabelProps={{ shrink: true }}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || (contractDates.start && contractDates.end 
                              ? `Contract: ${format(contractDates.start, 'MMM dd')} - ${format(contractDates.end, 'MMM dd, yyyy')}`
                              : '')}
                            value={field.value ? (field.value instanceof Date ? format(field.value, 'yyyy-MM-dd') : field.value) : ''}
                            onChange={(e) => {
                              const date = e.target.value ? parseISO(e.target.value) : undefined;
                              field.onChange(date);
                            }}
                            disabled={editingTimesheet?.status === 'invoiced'}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Controller
                        name={`entries.${index}.clientId`}
                        control={control}
                        rules={{ required: 'Client is required' }}
                        render={({ field, fieldState }) => (
                          <FormControl fullWidth error={!!fieldState.error} disabled={editingTimesheet?.status === 'invoiced'}>
                            <InputLabel>Client</InputLabel>
                            <Select {...field} label="Client">
                              {clients.map((client) => (
                                <MenuItem key={client.id} value={client.id}>
                                  {client.name} {client.customerType === 'Contract' ? '(Contract)' : '(T&M)'}
                                </MenuItem>
                              ))}
                            </Select>
                            {fieldState.error && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {fieldState.error.message}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Hours"
                        inputProps={{ step: 0.25, min: 0 }}
                        {...register(`entries.${index}.hours`, {
                          required: 'Hours is required',
                          valueAsNumber: true,
                          min: { value: 0, message: 'Hours must be positive' },
                        })}
                        error={!!errors.entries?.[index]?.hours}
                        helperText={errors.entries?.[index]?.hours?.message}
                        disabled={editingTimesheet?.status === 'invoiced'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label="Description"
                        {...register(`entries.${index}.projectDescription`, { required: 'Description is required' })}
                        error={!!errors.entries?.[index]?.projectDescription}
                        helperText={errors.entries?.[index]?.projectDescription?.message}
                        disabled={editingTimesheet?.status === 'invoiced'}
                      />
                    </Grid>
                    {company?.enableNonBillableTime && (
                      <>
                        <Grid item xs={12} sm={1}>
                          <Controller
                            name={`entries.${index}.isBillable`}
                            control={control}
                            render={({ field }) => (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    {...field}
                                    checked={field.value !== false}
                                    disabled={editingTimesheet?.status === 'invoiced'}
                                    color="primary"
                                  />
                                }
                                label="Billable"
                                sx={{ m: 0 }}
                              />
                            )}
                          />
                        </Grid>
                        {watch(`entries.${index}.isBillable`) === false && company?.nonBillableCategories && company.nonBillableCategories.length > 0 && (
                          <Grid item xs={12} sm={2}>
                            <Controller
                              name={`entries.${index}.category`}
                              control={control}
                              render={({ field }) => (
                                <FormControl fullWidth size="small" disabled={editingTimesheet?.status === 'invoiced'}>
                                  <InputLabel>Category</InputLabel>
                                  <Select {...field} label="Category">
                                    {(company.nonBillableCategories || []).map((cat) => (
                                      <MenuItem key={cat} value={cat}>
                                        {cat}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            />
                          </Grid>
                        )}
                      </>
                    )}
                    <Grid item xs={12} sm={1}>
                      {editingTimesheet?.status !== 'invoiced' && (
                        <IconButton onClick={() => remove(index)} color="error" size="small">
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                );
              })}

              {fields.length === 0 && (
                <Grid item xs={12}>
                  <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                    No entries. Click "Add Entry" to add timesheet entries.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            {editingTimesheet?.status !== 'invoiced' && (
              <Button type="submit" variant="contained">
                {editingTimesheet ? 'Update' : 'Create'}
              </Button>
            )}
          </DialogActions>
          </form>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Timesheet</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this timesheet? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Date Entries Dialog */}
      <Dialog open={dateEntriesDialogOpen} onClose={handleCloseDateEntriesDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Timesheet Entries for {selectedDate && format(selectedDate, 'MMMM dd, yyyy')}
        </DialogTitle>
        <DialogContent>
          {dateEntries.length === 0 ? (
            <Typography color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
              No entries found for this date.
            </Typography>
          ) : (
            <Box sx={{ mt: 2 }}>
              {dateEntries.map(({ entry, timesheet, entryIndex }, index) => {
                const isInvoiced = timesheet.status === 'invoiced';
                const key = `${timesheet.id}-${entryIndex}`;
                const edits = editedEntries.get(key) || {};
                const editedEntry = { ...entry, ...edits };
                const hasChanges = Object.keys(edits).length > 0;
                
                return (
                  <Paper key={`${timesheet.id}-${entryIndex}-${index}`} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: hasChanges ? 'primary.main' : 'divider' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small" disabled={isInvoiced}>
                          <InputLabel>Client</InputLabel>
                          <Select
                            value={editedEntry.clientId}
                            label="Client"
                            onChange={(e) => {
                              if (!isInvoiced) {
                                handleEntryFieldChange(timesheet.id, entryIndex, 'clientId', e.target.value);
                              }
                            }}
                          >
                            {clients.map((client) => (
                              <MenuItem key={client.id} value={client.id}>
                                {client.name} {client.customerType === 'Contract' ? '(Contract)' : '(T&M)'}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Hours"
                          value={editedEntry.hours}
                          inputProps={{ step: 0.25, min: 0 }}
                          disabled={isInvoiced}
                          onChange={(e) => {
                            if (!isInvoiced) {
                              const hours = parseFloat(e.target.value) || 0;
                              handleEntryFieldChange(timesheet.id, entryIndex, 'hours', hours);
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Description"
                          value={editedEntry.projectDescription}
                          disabled={isInvoiced}
                          onChange={(e) => {
                            if (!isInvoiced) {
                              handleEntryFieldChange(timesheet.id, entryIndex, 'projectDescription', e.target.value);
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Box display="flex" gap={1} justifyContent="flex-end" alignItems="center">
                          <Chip 
                            label={timesheet.status.charAt(0).toUpperCase() + timesheet.status.slice(1)} 
                            size="small" 
                            color={getStatusColor(timesheet.status) as any}
                          />
                          {!isInvoiced && hasChanges && (
                            <Tooltip title="Save Changes">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleSaveEntry(timesheet, entryIndex)}
                              >
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!isInvoiced && (
                            <Tooltip title="Delete Entry">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleDeleteEntry(timesheet, entryIndex)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                    {isInvoiced && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        This entry is part of an invoiced timesheet and cannot be edited or deleted.
                      </Alert>
                    )}
                    {hasChanges && !isInvoiced && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        You have unsaved changes. Click the save icon to save your changes.
                      </Alert>
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDateEntriesDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Timesheets;
